import React, { useState, useEffect } from 'react';
import { ToastContext } from '@/components/ToastProvider.jsx';
import { useAuth } from '../../AuthContext';
import { db } from '@/lib/firebase';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import PageBackground from '../../components/PageBackground';
import MapPicker from '@/components/MapPicker.jsx';

const deliveryBg = "https://images.unsplash.com/photo-1502086223501-7ea6ecd79368?auto=format&fit=crop&w=1500&q=80";

export default function DeliverySettings() {
  const { notify } = React.useContext(ToastContext);
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [geocoding, setGeocoding] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const mapsKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || (typeof window !== 'undefined' ? (window.PPAC_MAPS_KEY || '') : '');
  const [settings, setSettings] = useState({
    enabled: true,
    deliveryFeeType: 'fixed',
    fixedFee: 50,
    baseFee: 50,
    perKmRate: 10,
    maxDistance: 50,
    freeDeliveryThreshold: 1500,
    weightBasedFees: {
      enabled: false,
      baseWeight: 5, // kg
      baseWeightFee: 0, // no additional fee for base weight
      overweightThreshold: 10, // kg
      overweightFee: 20, // pesos per kg over threshold
      maxWeight: 50, // kg
      maxWeightFee: 100 // maximum weight fee (reduced from 500)
    },
    storeLocation: {
      lat: 14.5995,
      lng: 120.9842,
      address: 'Manila, Philippines'
    },
    deliveryZones: [
      {
        name: 'Zone 1 - Near Store',
        radius: 5,
        fee: 30,
        description: 'Within 5km of store'
      },
      {
        name: 'Zone 2 - Medium Distance',
        radius: 15,
        fee: 50,
        description: '5-15km from store'
      },
      {
        name: 'Zone 3 - Far Distance',
        radius: 50,
        fee: 100,
        description: '15-50km from store'
      }
    ],
    businessHours: {
      monday: { open: '08:00', close: '18:00' },
      tuesday: { open: '08:00', close: '18:00' },
      wednesday: { open: '08:00', close: '18:00' },
      thursday: { open: '08:00', close: '18:00' },
      friday: { open: '08:00', close: '18:00' },
      saturday: { open: '08:00', close: '16:00' },
      sunday: { open: '09:00', close: '14:00' }
    },
    deliveryTime: {
      estimatedMinutes: 60,
      maxMinutes: 120
    },
    restrictions: {
      minOrderAmount: 100,
      maxOrderAmount: 10000,
      excludedAreas: [],
      specialInstructions: 'Delivery available during business hours only'
    }
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const docRef = doc(db, 'settings', 'delivery');
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        if (data.settings) {
          // Merge database settings with defaults to ensure all fields are present
          const mergedSettings = {
            ...settings, // Start with defaults
            ...data.settings // Override with database values
          };
          setSettings(mergedSettings);
        }
      }
    } catch (error) {
              // Error loading delivery settings
      notify('Error loading delivery settings', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const docRef = doc(db, 'settings', 'delivery');
      await updateDoc(docRef, {
        settings: settings,
        updatedAt: serverTimestamp()
      });
      notify('Delivery settings saved successfully!', { type: 'success' });
    } catch (error) {
              // Error saving delivery settings
      notify('Error saving delivery settings', { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // Lazy-load Google Maps JS if not already loaded
  const ensureGoogleLoaded = async () => {
    if (window.google?.maps) return true;
    if (!mapsKey) {
      notify('To use address geocoding or map picker, set VITE_GOOGLE_MAPS_API_KEY and enable Maps JavaScript + Places API.', { type: 'error' });
      return false;
    }
    return await new Promise((resolve) => {
      const existing = document.querySelector('script[data-role="gmaps-loader"]');
      if (existing) {
        existing.addEventListener('load', () => resolve(true), { once: true });
        existing.addEventListener('error', () => resolve(false), { once: true });
        return;
      }
      const s = document.createElement('script');
  // Geocoding via Geocoder is included in Maps JS; Places is not required unless using Autocomplete
  s.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(mapsKey)}&loading=async&libraries=marker`;
      s.async = true;
      s.defer = true;
      s.setAttribute('data-role', 'gmaps-loader');
      s.onload = () => resolve(true);
      s.onerror = () => resolve(false);
      document.head.appendChild(s);
    });
  };

  const geocodeAddress = async () => {
    const addr = String(settings?.storeLocation?.address || '').trim();
    if (!addr) { notify('Enter a store address first.', { type: 'error' }); return; }
    setGeocoding(true);
    try {
      const ok = await ensureGoogleLoaded();
      if (!ok || !window.google?.maps) {
        notify('Google Maps JS not available. Check browser key and API enablement.', { type: 'error' });
        return;
      }
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ address: addr }, (results, status) => {
        if (status === 'OK' && results && results[0]) {
          const loc = results[0].geometry.location;
          const lat = typeof loc.lat === 'function' ? loc.lat() : loc.lat;
          const lng = typeof loc.lng === 'function' ? loc.lng() : loc.lng;
          updateSetting('storeLocation.lat', Number(lat));
          updateSetting('storeLocation.lng', Number(lng));
          // If the formatted address is present, prefer it
          const formatted = results[0].formatted_address || addr;
          updateSetting('storeLocation.address', formatted);
          notify('Coordinates updated from address.', { type: 'success' });
        } else {
          notify(`Geocoding failed: ${status}`, { type: 'error' });
        }
      });
    } catch (e) {
      notify('Geocoding error. Please try again.', { type: 'error' });
    } finally {
      setGeocoding(false);
    }
  };

  const updateSetting = (path, value) => {
    const keys = path.split('.');
    setSettings(prev => {
      const newSettings = { ...prev };
      let current = newSettings;
      
      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) {
          current[keys[i]] = {};
        }
        current = current[keys[i]];
      }
      
      current[keys[keys.length - 1]] = value;
      return newSettings;
    });
  };

  const addDeliveryZone = () => {
    const newZone = {
      name: `Zone ${settings.deliveryZones.length + 1}`,
      radius: 10,
      fee: 50,
      description: 'New delivery zone'
    };
    setSettings(prev => ({
      ...prev,
      deliveryZones: [...prev.deliveryZones, newZone]
    }));
  };

  const removeDeliveryZone = (index) => {
    setSettings(prev => ({
      ...prev,
      deliveryZones: prev.deliveryZones.filter((_, i) => i !== index)
    }));
  };

  const updateDeliveryZone = (index, field, value) => {
    setSettings(prev => ({
      ...prev,
      deliveryZones: prev.deliveryZones.map((zone, i) => 
        i === index ? { ...zone, [field]: value } : zone
      )
    }));
  };

  const updateBusinessHours = (day, field, value) => {
    setSettings(prev => ({
      ...prev,
      businessHours: {
        ...prev.businessHours,
        [day]: {
          ...prev.businessHours[day],
          [field]: value
        }
      }
    }));
  };

  if (loading) {
    return (
      <PageBackground image={deliveryBg} boxed boxedWidth="max-w-6xl" overlayClass="bg-white/85 backdrop-blur" className="page-gutter">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin w-8 h-8 border-4 border-brand-600 border-t-transparent rounded-full"></div>
        </div>
      </PageBackground>
    );
  }

  return (
    <PageBackground image={deliveryBg} boxed boxedWidth="max-w-6xl" overlayClass="bg-white/85 backdrop-blur" className="page-gutter">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-ink">Delivery Settings</h1>
        <p className="text-ink/70 mt-2">Configure delivery fees, zones, and delivery policies</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Basic Settings */}
        <div className="card p-6">
          <h2 className="text-xl font-semibold mb-4">Basic Settings</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-ink/80 mb-2">
                <input
                  type="checkbox"
                  checked={settings.enabled}
                  onChange={(e) => updateSetting('enabled', e.target.checked)}
                  className="mr-2"
                />
                Enable Delivery Service
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium text-ink/80 mb-2">Delivery Fee Type</label>
              <select
                value={settings.deliveryFeeType}
                onChange={(e) => updateSetting('deliveryFeeType', e.target.value)}
                className="input w-full"
              >
                <option value="fixed">Fixed Fee</option>
                <option value="distance">Distance-based</option>
                <option value="zones">Zone-based</option>
                <option value="free">Free Delivery</option>
              </select>
            </div>

            {settings.deliveryFeeType === 'fixed' && (
              <div>
                <label className="block text-sm font-medium text-ink/80 mb-2">Fixed Delivery Fee (₱)</label>
                <input
                  type="number"
                  value={settings.fixedFee}
                  onChange={(e) => updateSetting('fixedFee', Number(e.target.value))}
                  className="input w-full"
                  min="0"
                  step="0.01"
                />
              </div>
            )}

            {settings.deliveryFeeType === 'distance' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-ink/80 mb-2">Base Fee (₱)</label>
                  <input
                    type="number"
                    value={settings.baseFee}
                    onChange={(e) => updateSetting('baseFee', Number(e.target.value))}
                    className="input w-full"
                    min="0"
                    step="0.01"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-ink/80 mb-2">Per KM Rate (₱)</label>
                  <input
                    type="number"
                    value={settings.perKmRate}
                    onChange={(e) => updateSetting('perKmRate', Number(e.target.value))}
                    className="input w-full"
                    min="0"
                    step="0.01"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-ink/80 mb-2">Max Distance (km)</label>
                  <input
                    type="number"
                    value={settings.maxDistance}
                    onChange={(e) => updateSetting('maxDistance', Number(e.target.value))}
                    className="input w-full"
                    min="0"
                    step="0.01"
                  />
                </div>
              </>
            )}

                         <div>
               <label className="block text-sm font-medium text-ink/80 mb-2">Free Delivery Threshold (₱)</label>
               <input
                 type="number"
                 value={settings.freeDeliveryThreshold}
                 onChange={(e) => updateSetting('freeDeliveryThreshold', Number(e.target.value))}
                 className="input w-full"
                 min="0"
                 step="0.01"
               />
               <p className="text-xs text-ink/60 mt-1">Orders above this amount get free delivery</p>
             </div>
           </div>
         </div>

         {/* Weight-Based Fees */}
         <div className="card p-6">
           <h2 className="text-xl font-semibold mb-4">Weight-Based Fees</h2>
           
           <div className="space-y-4">
             <div>
               <label className="block text-sm font-medium text-ink/80 mb-2">
                 <input
                   type="checkbox"
                   checked={settings.weightBasedFees?.enabled || false}
                   onChange={(e) => updateSetting('weightBasedFees.enabled', e.target.checked)}
                   className="mr-2"
                 />
                 Enable Weight-Based Fees
               </label>
               <p className="text-xs text-ink/60 mt-1">Add extra fees based on order weight</p>
             </div>

             {settings.weightBasedFees?.enabled && (
               <>
                 <div>
                   <label className="block text-sm font-medium text-ink/80 mb-2">Base Weight (kg)</label>
                   <input
                     type="number"
                     value={settings.weightBasedFees?.baseWeight || 5}
                     onChange={(e) => updateSetting('weightBasedFees.baseWeight', Number(e.target.value))}
                     className="input w-full"
                     min="0"
                     step="0.1"
                   />
                   <p className="text-xs text-ink/60 mt-1">No additional fee for orders up to this weight</p>
                 </div>

                 <div>
                   <label className="block text-sm font-medium text-ink/80 mb-2">Overweight Threshold (kg)</label>
                   <input
                     type="number"
                     value={settings.weightBasedFees?.overweightThreshold || 10}
                     onChange={(e) => updateSetting('weightBasedFees.overweightThreshold', Number(e.target.value))}
                     className="input w-full"
                     min="0"
                     step="0.1"
                   />
                   <p className="text-xs text-ink/60 mt-1">Start charging extra fees after this weight</p>
                 </div>

                 <div>
                   <label className="block text-sm font-medium text-ink/80 mb-2">Overweight Fee per kg (₱)</label>
                   <input
                     type="number"
                     value={settings.weightBasedFees?.overweightFee || 20}
                     onChange={(e) => updateSetting('weightBasedFees.overweightFee', Number(e.target.value))}
                     className="input w-full"
                     min="0"
                     step="0.01"
                   />
                   <p className="text-xs text-ink/60 mt-1">Additional fee per kg over the threshold</p>
                 </div>

                 <div>
                   <label className="block text-sm font-medium text-ink/80 mb-2">Maximum Weight (kg)</label>
                   <input
                     type="number"
                     value={settings.weightBasedFees?.maxWeight || 50}
                     onChange={(e) => updateSetting('weightBasedFees.maxWeight', Number(e.target.value))}
                     className="input w-full"
                     min="0"
                     step="0.1"
                   />
                   <p className="text-xs text-ink/60 mt-1">Maximum weight for delivery</p>
                 </div>

                 <div>
                   <label className="block text-sm font-medium text-ink/80 mb-2">Maximum Weight Fee (₱)</label>
                   <input
                     type="number"
                     value={settings.weightBasedFees?.maxWeightFee || 100}
                     onChange={(e) => updateSetting('weightBasedFees.maxWeightFee', Number(e.target.value))}
                     className="input w-full"
                     min="0"
                     step="0.01"
                   />
                   <p className="text-xs text-ink/60 mt-1">Maximum fee regardless of weight</p>
                 </div>

                 <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                   <h3 className="font-medium text-blue-900 mb-2">Weight Fee Calculation Example:</h3>
                   <div className="text-sm text-blue-800 space-y-1">
                     <p>• Base weight (0-{settings.weightBasedFees?.baseWeight || 5}kg): No extra fee</p>
                     <p>• Overweight ({settings.weightBasedFees?.overweightThreshold || 10}kg+): +₱{settings.weightBasedFees?.overweightFee || 20} per kg</p>
                     <p>• Example: 15kg order = +₱{Math.max(0, (15 - (settings.weightBasedFees?.overweightThreshold || 10)) * (settings.weightBasedFees?.overweightFee || 20))} extra</p>
                     <p>• Maximum fee: ₱{settings.weightBasedFees?.maxWeightFee || 100}</p>
                   </div>
                 </div>
               </>
             )}
           </div>
         </div>

        {/* Store Location */}
        <div className="card p-6">
          <h2 className="text-xl font-semibold mb-4">Store Location</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-ink/80 mb-2">Latitude</label>
              <input
                type="number"
                value={settings.storeLocation.lat}
                onChange={(e) => updateSetting('storeLocation.lat', Number(e.target.value))}
                className="input w-full"
                step="0.000001"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-ink/80 mb-2">Longitude</label>
              <input
                type="number"
                value={settings.storeLocation.lng}
                onChange={(e) => updateSetting('storeLocation.lng', Number(e.target.value))}
                className="input w-full"
                step="0.000001"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-ink/80 mb-2">Address</label>
              <input
                type="text"
                value={settings.storeLocation.address}
                onChange={(e) => updateSetting('storeLocation.address', e.target.value)}
                className="input w-full"
                placeholder="Store address"
              />
              <div className="mt-2 flex flex-wrap gap-2">
                <button
                  type="button"
                  className="btn btn-sm btn-outline"
                  onClick={geocodeAddress}
                  disabled={geocoding}
                >
                  {geocoding ? 'Finding coordinates…' : 'Use address → coordinates'}
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline"
                  onClick={async () => {
                    const ok = await ensureGoogleLoaded();
                    if (ok) setShowMap((v) => !v);
                  }}
                >
                  {showMap ? 'Hide map picker' : 'Pick on map'}
                </button>
              </div>
              {showMap && (
                <div className="mt-3 border rounded p-2">
                  {mapsKey ? (
                    <MapPicker
                      lat={settings.storeLocation.lat}
                      lng={settings.storeLocation.lng}
                      apiKey={mapsKey}
                      onChange={(la, ln) => {
                        updateSetting('storeLocation.lat', la);
                        updateSetting('storeLocation.lng', ln);
                      }}
                    />
                  ) : (
                    <div className="rounded border bg-gray-50 p-2 text-xs text-ink/60">
                      To enable the map picker, set VITE_GOOGLE_MAPS_API_KEY in your environment and enable Maps JavaScript + Places API.
                    </div>
                  )}
                  <div className="text-xs text-ink/60 mt-2">
                    Current: {Number(settings.storeLocation.lat || 0).toFixed(6)}, {Number(settings.storeLocation.lng || 0).toFixed(6)}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Delivery Zones */}
        <div className="card p-6 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">Delivery Zones</h2>
            <button
              onClick={addDeliveryZone}
              className="btn btn-primary btn-sm"
            >
              Add Zone
            </button>
          </div>
          
          <div className="space-y-4">
            {settings.deliveryZones.map((zone, index) => (
              <div key={index} className="border rounded-lg p-4">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-ink/80 mb-2">Zone Name</label>
                    <input
                      type="text"
                      value={zone.name}
                      onChange={(e) => updateDeliveryZone(index, 'name', e.target.value)}
                      className="input w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-ink/80 mb-2">Radius (km)</label>
                    <input
                      type="number"
                      value={zone.radius}
                      onChange={(e) => updateDeliveryZone(index, 'radius', Number(e.target.value))}
                      className="input w-full"
                      min="0"
                      step="0.1"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-ink/80 mb-2">Fee (₱)</label>
                    <input
                      type="number"
                      value={zone.fee}
                      onChange={(e) => updateDeliveryZone(index, 'fee', Number(e.target.value))}
                      className="input w-full"
                      min="0"
                      step="0.01"
                    />
                  </div>
                  <div className="flex items-end">
                    <button
                      onClick={() => removeDeliveryZone(index)}
                      className="btn btn-rose btn-sm w-full"
                    >
                      Remove
                    </button>
                  </div>
                </div>
                <div className="mt-3">
                  <label className="block text-sm font-medium text-ink/80 mb-2">Description</label>
                  <input
                    type="text"
                    value={zone.description}
                    onChange={(e) => updateDeliveryZone(index, 'description', e.target.value)}
                    className="input w-full"
                    placeholder="Zone description"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Business Hours */}
        <div className="card p-6">
          <h2 className="text-xl font-semibold mb-4">Business Hours</h2>
          
          <div className="space-y-3">
            {Object.entries(settings.businessHours).map(([day, hours]) => (
              <div key={day} className="flex items-center gap-3">
                <div className="w-20 text-sm font-medium text-ink/80 capitalize">
                  {day}
                </div>
                <input
                  type="time"
                  value={hours.open}
                  onChange={(e) => updateBusinessHours(day, 'open', e.target.value)}
                  className="input flex-1"
                />
                <span className="text-ink/60">to</span>
                <input
                  type="time"
                  value={hours.close}
                  onChange={(e) => updateBusinessHours(day, 'close', e.target.value)}
                  className="input flex-1"
                />
              </div>
            ))}
          </div>
        </div>

        {/* Delivery Time & Restrictions */}
        <div className="card p-6">
          <h2 className="text-xl font-semibold mb-4">Delivery Time & Restrictions</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-ink/80 mb-2">Estimated Delivery Time (minutes)</label>
              <input
                type="number"
                value={settings.deliveryTime.estimatedMinutes}
                onChange={(e) => updateSetting('deliveryTime.estimatedMinutes', Number(e.target.value))}
                className="input w-full"
                min="0"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-ink/80 mb-2">Max Delivery Time (minutes)</label>
              <input
                type="number"
                value={settings.deliveryTime.maxMinutes}
                onChange={(e) => updateSetting('deliveryTime.maxMinutes', Number(e.target.value))}
                className="input w-full"
                min="0"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-ink/80 mb-2">Minimum Order Amount (₱)</label>
              <input
                type="number"
                value={settings.restrictions.minOrderAmount}
                onChange={(e) => updateSetting('restrictions.minOrderAmount', Number(e.target.value))}
                className="input w-full"
                min="0"
                step="0.01"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-ink/80 mb-2">Maximum Order Amount (₱)</label>
              <input
                type="number"
                value={settings.restrictions.maxOrderAmount}
                onChange={(e) => updateSetting('restrictions.maxOrderAmount', Number(e.target.value))}
                className="input w-full"
                min="0"
                step="0.01"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-ink/80 mb-2">Special Instructions</label>
              <textarea
                value={settings.restrictions.specialInstructions}
                onChange={(e) => updateSetting('restrictions.specialInstructions', e.target.value)}
                className="input w-full"
                rows="3"
                placeholder="Special delivery instructions"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="mt-8 flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="btn btn-primary btn-lg"
        >
          {saving ? 'Saving...' : 'Save Delivery Settings'}
        </button>
      </div>
    </PageBackground>
  );
}
